import type { Assignment, AssignmentKind } from './types';

// Words and icons for each kind of assignment, in one place.
export const KIND: Record<AssignmentKind, {
  icon: string; label: string; verb: string; thisWeek: string; doneWord: string;
}> = {
  video: { icon: '🎬', label: 'Video', verb: 'watch', thisWeek: 'Open video', doneWord: 'played' },
  story: { icon: '📖', label: 'Story', verb: 'read', thisWeek: 'Open story', doneWord: 'reading' },
  grammar: { icon: '🧩', label: 'Grammar', verb: 'lesson', thisWeek: 'Open grammar', doneWord: 'practising' },
};

/** "Watch 3 times" · "Read once" · "3 lessons" */
export function requirement(a: Pick<Assignment, 'kind' | 'required_count' | 'one_per_day'>, short = false): string {
  const n = a.required_count;
  const daily = a.one_per_day && n > 1 ? (short ? ' · 1/day' : ', one a day') : '';
  if (a.kind === 'grammar') return `${n} lesson${n === 1 ? '' : 's'}${daily}`;
  const verb = a.kind === 'video' ? 'Watch' : 'Read';
  if (short) return `${verb} ${n}×${daily}`;
  return `${verb} ${n === 1 ? 'once' : `${n} times`}${daily}`;
}

/** "watch" / "reads" / "lessons" */
export function passWord(kind: AssignmentKind, n = 1): string {
  const one = KIND[kind].verb;
  return n === 1 ? one : kind === 'video' ? 'watches' : `${one}s`;
}

/** The day an assignment goes out to students (YYYY-MM-DD). */
export const goesOutOn = (a: Pick<Assignment, 'week_start' | 'publish_on'>): string =>
  a.publish_on ?? a.week_start;

/** Sent to students yet? Drafts never are; scheduled ones are from their day. */
export const isOut = (a: Pick<Assignment, 'week_start' | 'publish_on' | 'draft'>, today: string): boolean =>
  !a.draft && goesOutOn(a) <= today;

// ── Open, by due date (Josiah, 2026-10-28: no more "weeks") ─────────────────
// An assignment is open from the day it goes out until its due date (a
// student's extension can keep it open just for them). Every assignment has a
// due date (the form requires one; every older assignment already had one).

/** Still open for anyone: sent out, and not past its due date (or any extension of it). */
export function isOpen(a: Pick<Assignment, 'week_start' | 'publish_on' | 'draft' | 'due_date'>, today: string, extensionDues: (string | null | undefined)[] = []): boolean {
  if (!isOut(a, today)) return false;
  return !a.due_date || a.due_date >= today || extensionDues.some(d => !!d && d >= today);
}

/** Soonest due first (no due date last), then oldest first. */
export const byDue = (a: Pick<Assignment, 'due_date' | 'created_at'>, b: Pick<Assignment, 'due_date' | 'created_at'>): number =>
  (a.due_date ?? '9999').localeCompare(b.due_date ?? '9999') || a.created_at.localeCompare(b.created_at);
